/**
 * „Do przejrzenia" — the queue, and the sentence that says how far back it goes.
 *
 * ── The submission opens in the CLIENT DETAIL's report, in a modal ───────────────────────────
 * Owner, 17 September 2026: „jak ktoś kliknie Podgląd, to podejrzy w DOKŁADNIE tym samym widoku
 * ankietę, w modalu." So the eye opens `features/survey-report`'s dialog, which draws the same
 * component the client's own Ankiety tab draws, from the same read — deltas, units, photographs and
 * the clip included. The right-hand pane that used to stand here is gone with its
 * `GET /survey/assignment/{id}`: two renderings of one submission, one of them poorer, is the
 * duplication the house rule exists to stop. The list takes the full width and keeps its reserve,
 * so removing the pane moved nothing about how the rows load.
 *
 * ── The window is SAID OUT LOUD, from the response's own field ───────────────────────────────
 * „Lista sięga 30 dni wstecz. Przejrzane znikają z niej, ale zostają w historii klienta." A trainer
 * looking at an empty queue has to know whether that means nothing is waiting or that the older
 * things have simply scrolled out of a window. The figure is the backend's, never a constant here —
 * `reviewQueue`'s header carries the reasoning — and `isTruncated` gets its own plain sentence.
 *
 * ── The foot reserves both lines from the first frame ────────────────────────────────────────
 * Both sentences arrive with the read. A foot that grew a line when they did would move the card's
 * bottom edge, which is the whole of the no-jump policy applied to a two-line box.
 *
 * ── Reviewing removes the row from HERE and from nowhere else ────────────────────────────────
 * `features/survey-review` invalidates the queue's prefix and the attention queue; the client's own
 * history is a different read and keeps the occurrence for ever. Nothing is deleted locally, so a
 * write that failed leaves the row exactly where it was — and says so in the card's foot, which is
 * where the two acts that can fail both stand now.
 *
 * ── Focus after a mark, from either control ──────────────────────────────────────────────────
 * A reviewed row leaves the list, so whichever control was pressed is about to be destroyed. From
 * the list, focus is moved to the row that will take its place before the refetch. From the dialog,
 * the same row is remembered and handed to Base UI's `finalFocus`, which asks for it at close time
 * — by then the list has already been rebuilt, so a ref answers and a captured element would not.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SurveyReportDialog } from '~/features/survey-report';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DoneMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import ghost from '~/shared/ui/ghostButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import type { ReviewRow } from '../model/reviewQueue';
import { useReviewTab } from '../lib/useReviewTab';
import { ReviewQueueList, reviewRowId } from './ReviewQueueList';
import styles from './ReviewQueue.module.css';

/** Five rows, the canon's own case, and what `--h-report-queue-min` was derived from. */
const SKELETON_ROWS = 5;

function QueueSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={styles.list} aria-busy="true">
      <p className={hidden.srOnly}>{t('web:reports.queue.states.loading')}</p>
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <div key={row} className={styles.skelRow}>
          {skeleton ? (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelFace)}`} />
              <span className={styles.skelText}>
                <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelSummary)}`} />
              </span>
              <i className={`${String(skel.bar)} ${String(styles.skelWhen)}`} />
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function ReviewQueueTab() {
  const { t } = useTranslation();
  const announcer = useAnnouncer();
  const review = useReviewTab();
  /** The row a mark made from inside the dialog should hand focus back to. See the header. */
  const focusAfterClose = useRef<number | null>(null);

  const { announce } = announcer;

  /** The submission that will stand where this one does once it leaves the list. */
  const successor = (row: ReviewRow): ReviewRow | null => {
    const index = review.queue.rows.findIndex((entry) => entry.assignmentId === row.assignmentId);
    return review.queue.rows[index + 1] ?? review.queue.rows[index - 1] ?? null;
  };

  const said = (row: ReviewRow) =>
    t('web:reports.queue.marked', { client: row.name, survey: row.templateTitle });

  const markFromList = (row: ReviewRow) => {
    review.markReviewed(row.assignmentId, () => {
      announce(said(row));
      // The row is about to be removed by the refetch and the focus ring is on one of its controls.
      const next = successor(row);
      if (next !== null) document.getElementById(reviewRowId(next.assignmentId))?.focus();
    });
  };

  return (
    <div className={styles.tab}>
      <div className={styles.card}>
        {review.listState === 'pending' ? (
          <QueueSkeleton skeleton={review.listSkeleton} />
        ) : review.listState === 'rows' ? (
          <ReviewQueueList
            rows={review.queue.rows}
            openedId={review.opened?.assignmentId ?? null}
            onOpen={review.onOpen}
            onMark={markFromList}
            marking={review.marking}
            reveal={review.listReveal}
          />
        ) : (
          <div className={styles.state}>
            {review.listState === 'error' ? (
              <>
                <EmptyState
                  mark={<NoteMarkIcon />}
                  tone="neutral"
                  title={t('web:reports.queue.states.errorTitle')}
                  hint={t('web:reports.queue.states.errorMessage')}
                />
                <button
                  type="button"
                  className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                  onClick={review.onRetry}
                >
                  {t('web:reports.queue.states.retry')}
                </button>
              </>
            ) : (
              // Nothing waiting is GOOD NEWS for a trainer and the tone says so. There is one empty
              // here since the kind filter went (owner, 17 September 2026): „this slice of the queue
              // is empty" was a second, neutral one, and it had nothing left to be about.
              <EmptyState
                mark={<DoneMarkIcon />}
                tone="positive"
                title={t('web:reports.queue.empty.title')}
                hint={t('web:reports.queue.empty.message')}
              />
            )}
          </div>
        )}

        <div className={styles.foot}>
          {/* A mark that did not reach the backend, beside the list whose tick tried it. The dialog
            * says it in its own head, next to its own control. */}
          <span className={styles.footLine}>
            {review.markFailed ? (
              <span className={styles.failed} role="alert">
                {t('web:reports.queue.markFailed')}
              </span>
            ) : (
              review.windowNote
            )}
          </span>
          <span className={styles.footLine}>
            {review.queue.truncated ? t('web:reports.queue.truncated') : null}
          </span>
        </div>
      </div>

      <SurveyReportDialog
        review={{
          markReviewed: review.markReviewed,
          marking: review.marking,
          markFailed: review.markFailed,
        }}
        request={
          review.opened === null
            ? null
            : {
                kind: 'occurrence',
                customerId: review.opened.customerId,
                assignmentId: review.opened.assignmentId,
                clientName: review.opened.name,
              }
        }
        onClose={review.onCloseReport}
        onMarked={() => {
          const row = review.opened;
          if (row === null) return;
          announce(said(row));
          focusAfterClose.current = successor(row)?.assignmentId ?? null;
          // The row is leaving the queue, so the dialog describing it closes with it rather than
          // standing over a list the submission is no longer in.
          review.onCloseReport();
        }}
        finalFocus={() =>
          focusAfterClose.current === null
            ? null
            : document.getElementById(reviewRowId(focusAfterClose.current))
        }
      />

      <LiveRegionAnnouncer message={announcer.message} />
    </div>
  );
}
